import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sessionsApi, activityApi, aiApi } from '../services/api';
import { io } from 'socket.io-client';

export function DashboardPage() {
  const [todaySessions, setTodaySessions] = useState<any[]>([]);
  const [liveStatus, setLiveStatus] = useState<any[]>([]);
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30s

    // Real-time socket
    const token = localStorage.getItem('at_token');
    const socket = io('/track', { auth: { token }, transports: ['websocket'] });

    socket.on('user:clock_in', () => loadData());
    socket.on('user:clock_out', () => loadData());
    socket.on('user:heartbeat', (data: any) => {
      setLiveStatus(prev => {
        const idx = prev.findIndex(u => u.userId === data.userId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...data };
          return updated;
        }
        return prev;
      });
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, []);

  const loadData = async () => {
    try {
      const [sessions, live, anom] = await Promise.all([
        sessionsApi.getToday(),
        activityApi.getLive(),
        aiApi.getAnomalies(),
      ]);
      setTodaySessions(sessions);
      setLiveStatus(live);
      setAnomalies(anom);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const activeSessions = todaySessions.filter(s => s.status === 'active');
  const closedSessions = todaySessions.filter(s => s.status === 'closed');
  const totalHours = todaySessions.reduce((sum, s) => sum + (s.totalSeconds || 0), 0);

  const formatHours = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  };

  return (
    <div style={{ padding: '28px 32px' }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={loadData}>🔄 Actualizar</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            🟢 Activos ahora
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--green)' }}>{activeSessions.length}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>empleados trabajando</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            ✅ Han fichado hoy
          </div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>{todaySessions.length}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{closedSessions.length} ya salieron</div>
        </div>
        <div className="card">
          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            ⏱️ Horas totales hoy
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--accent)' }}>{formatHours(totalHours)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>entre todos los empleados</div>
        </div>
        <div className="card" style={{ borderColor: anomalies.length > 0 ? 'rgba(239,68,68,0.3)' : undefined }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            🚨 Alertas
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: anomalies.length > 0 ? 'var(--red)' : 'var(--text3)' }}>
            {anomalies.length}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>requieren atención</div>
        </div>
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div className="card" style={{ marginBottom: 24, borderColor: 'rgba(239,68,68,0.25)', background: 'var(--red-dim)' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'var(--red)' }}>🚨 Alertas del sistema</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {anomalies.map((a, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--text2)' }}>
                • {a.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Active employees */}
        <div className="card">
          <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>👥 Actividad en tiempo real</h3>
          {loading ? (
            <div style={{ color: 'var(--text3)', fontSize: 12 }}>Cargando...</div>
          ) : activeSessions.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
              Nadie trabajando ahora mismo
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {activeSessions.map((session: any) => {
                const live = liveStatus.find(l => l.userId === session.userId);
                const elapsed = Math.floor((Date.now() - new Date(session.clockIn).getTime()) / 1000);
                const h = Math.floor(elapsed / 3600);
                const m = Math.floor((elapsed % 3600) / 60);
                return (
                  <Link key={session.id} to={`/employees/${session.userId}`} style={{ textDecoration: 'none' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      background: 'var(--bg3)',
                      borderRadius: 10,
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}>
                      <div style={{
                        width: 32, height: 32,
                        borderRadius: '50%',
                        background: 'var(--green-dim)',
                        color: 'var(--green)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flexShrink: 0,
                      }}>
                        {session.user?.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{session.user?.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {live?.currentApp || 'Sin actividad'} · {h}h {m}m
                        </div>
                      </div>
                      <span className="dot dot-green" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Today's clock-ins */}
        <div className="card">
          <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>📋 Fichajes de hoy</h3>
          {todaySessions.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>
              Sin fichajes hoy
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {todaySessions.map((session: any) => {
                const clockIn = new Date(session.clockIn).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                const clockOut = session.clockOut
                  ? new Date(session.clockOut).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
                  : null;
                return (
                  <div key={session.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--border2)',
                  }}>
                    <span className={`dot ${session.status === 'active' ? 'dot-green' : 'dot-gray'}`} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{session.user?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                        Entrada: {clockIn}{clockOut ? ` · Salida: ${clockOut}` : ' · Trabajando'}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatHours(session.totalSeconds || 0)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
